import { useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import useBackendApi from '../hooks/useBackendApi';
import { RequestOptions } from '../types/httpRequests';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import { colors } from '../styles/fontStyles';
import { CompoundCardProps } from '../types/reports';
import CompoundDetailsChart from '../components/charts/CompoundDetailsChart';
import BorderedButton from '../components/common/buttons/BorderedButton';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import { CompoundDetailsPagination } from './tabsContent/CompoundDetailsPagination';
import { CompoundChartChipMeaning } from './tabsContent/CompoundChartChipMeaning';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

const backendURL = process.env.REACT_APP_BACKEND_URL;

type CompoundDetailsResultProps = {
  detectionFreq: string;
  setIsCompoundDetailsLoading: (value: boolean) => void;
  setIsDescComponentOpen: (value: boolean) => void;
  setSelectedCompoundIndex: (value: number) => void;
  sortedCompoundDataList: CompoundCardProps[];
  selectedCompoundIndex: number;
  showPagination?: boolean;
};

const CompoundDetailsResult = ({
  showPagination = true,
  setIsDescComponentOpen,
  detectionFreq,
  setIsCompoundDetailsLoading,
  sortedCompoundDataList,
  setSelectedCompoundIndex,
  selectedCompoundIndex,
}: CompoundDetailsResultProps) => {
  const { theme } = useThemeContext();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { apiFetchRequest } = useBackendApi();
  const [reportDescription, setReportDescription] = useState<any>('');
  const [reportDescriptionLoading, setReportDescriptionLoading] = useState<boolean>(true);
  const [activeDdfv, setActiveDdfv] = useState(sortedCompoundDataList[selectedCompoundIndex].ddfv);
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const helpId = activeDdfv?.testNameAbbr;

  useEffect(() => {
    setActiveDdfv(sortedCompoundDataList[selectedCompoundIndex].ddfv);
  }, [selectedCompoundIndex, sortedCompoundDataList]);

  useEffect(() => {
    const handleFetchReportById = async () => {
      const endpointUrl: string = `${backendURL}/help/${helpId}`;
      const apiRequestOptions: RequestOptions = {
        endpoint: endpointUrl,
      };

      const data = await apiFetchRequest(apiRequestOptions);
      setIsCompoundDetailsLoading(false);

      if (data.status !== 200) {
        addAlertMessages({
          message: 'Description not found.',
          severityType: 'error',
        });
        setReportDescriptionLoading(false);
        return;
      }

      setReportDescription(data.body.h);
      setReportDescriptionLoading(false);
    };

    handleFetchReportById();
  }, [helpId, apiFetchRequest]);

  const renderGraphDetails = () => {
    if (!activeDdfv) return;

    const componentDetails = {
      nameAbbr: activeDdfv.testNameAbbr,
      testNameOnly: activeDdfv.testNameOnly,
      concentrationValue: activeDdfv.value,
      detectionFreq: detectionFreq,
      value: activeDdfv.valueAsFloat,
      lowerReportableValue: parseFloat(activeDdfv.lowerReportableValue),
      higherReportableValue: parseFloat(activeDdfv.higherReportableValue),
      label: activeDdfv.label,
      color: activeDdfv?.color || '',
      fillFontColor: activeDdfv?.fillFontColor || '',
      detectionStatus: activeDdfv.detectionStatus,
    };

    return (
      <Box>
        <CompoundDetailsChart compoundDetails={componentDetails} />
      </Box>
    );
  };

  const renderCompoundReportDetails = () => {
    if (reportDescriptionLoading) {
      return (
        <Box sx={{ height: '200px' }}>
          <GradientCircularProgressContainer loadingMessage={'Loading compound details'} />
        </Box>
      );
    }

    return (
      <>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              variant="h5"
              sx={{
                color:
                  theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderCompoundReportDetails
                    .testNameAbbrColor,
                fontSize: '36px',
                fontWeight: 400,
                mt: { xs: 2, md: 0 },
              }}
            >
              {activeDdfv.testNameAbbr}
            </Typography>
            {isDesktop && renderBackToCompoundsListBtn()}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color:
                theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderCompoundReportDetails
                  .testNameOnlyColor,
              fontSize: '19px',
              fontWeight: 400,
            }}
          >
            {activeDdfv.testNameOnly}
          </Typography>
        </Box>
        {isDesktop && (
          <CompoundChartChipMeaning
            lowerReportableValue={parseFloat(activeDdfv.lowerReportableValue)}
            higherReportableValue={parseFloat(activeDdfv.higherReportableValue)}
          />
        )}
        <Box
          sx={{
            color:
              theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderCompoundReportDetails.boxStyles
                .color,
            marginBottom: '16px',
            lineHeight: 1.5,
            flexGrow: 1,
            '& h3': {
              color:
                theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderCompoundReportDetails
                  .boxStyles.h3color,
              fontWeight: 400,
              fontSize: '22px',
              marginBottom: '12px',
            },
            '& h4': {
              color:
                theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderCompoundReportDetails
                  .boxStyles.h4Color,
              fontWeight: 400,
              fontSize: '22px',
              marginBottom: '8px',
            },
            '& p': {
              color:
                theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderCompoundReportDetails
                  .boxStyles.pColor,
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              marginBottom: '24px',
            },
          }}
          dangerouslySetInnerHTML={{ __html: reportDescription }}
        />
      </>
    );
  };

  const renderBackToCompoundsListBtn = () => {
    if (!showPagination) return <></>;

    const backButtonText = isDesktop ? 'Back to All compounds' : 'Compounds';

    return (
      <BorderedButton
        sxProps={{
          alignItems: 'center',
          alignSelf: 'stretch',
          display: 'flex',
          justifyContent: 'center',
          lineHeight: '17px',
          marginLeft: '0px',
          marginTop: '12px',
          mb: 2,
          color: isDesktop
            ? theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderBackToCompoundsListBtn
                .desktopBackButtonColor
            : theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderBackToCompoundsListBtn
                .mobileBackButtonColor,
          '& .MuiButton-startIcon': {
            height: '18px',
            width: '18px',
            backgroundImage: `url(${isDesktop ? theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderBackToCompoundsListBtn.default.endIcon : theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderBackToCompoundsListBtn.default.startIcon})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          },
          '&:hover': {
            background: 'transparent',
            color: theme.buttons.ghost.hover.color,
            border: '2px solid',
            '& .MuiButton-startIcon': {
              height: '18px',
              width: '18px',
              backgroundImage: `url(${isDesktop ? theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderBackToCompoundsListBtn.hover.endIcon : theme.custom.report.details.compoundCardsList.compoundDetailsResult.renderBackToCompoundsListBtn.hover.startIcon})`,
              backgroundSize: 'contain',
            },
          },
        }}
        additionalProps={{
          style: { border: 'none', height: '18px' },
          startIcon: <></>,
        }}
        onClick={() => setIsDescComponentOpen(false)}
        buttonText={backButtonText}
      />
    );
  };

  const renderCompoundDtailsPagination = () => {
    if (!showPagination) return <></>;

    return (
      <CompoundDetailsPagination
        setReportDescriptionLoading={setReportDescriptionLoading}
        setSelectedCompoundIndex={setSelectedCompoundIndex}
        sortedCompoundDataList={sortedCompoundDataList}
        selectedCompoundIndex={selectedCompoundIndex}
      />
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.custom.report.details.compoundCardsList.compoundDetailsResult.backgroundColor,
        borderRadius: '8px',
        padding: '15px 18px',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {!isDesktop && renderBackToCompoundsListBtn()}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ width: { xs: '100%', md: '27% ' } }}>{renderGraphDetails()}</Box>
        <Box sx={{ width: { xs: '100%', md: '73%' } }}>{renderCompoundReportDetails()}</Box>
      </Box>
      {renderCompoundDtailsPagination()}
      {renderSnackBarAlertComponent()}
    </Box>
  );
};

export default CompoundDetailsResult;
